// @client/components/profile-manager/RelationsPills.tsx
// Clean rewrite - Type-safe with Core-Types

import React from "react";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import { Edit3 } from "lucide-react";

import type { ProfileCategoryInfo, ProfileListInfo } from "@shared/types";

// Generic Pills Component (internal use only)
interface RelationsPillsProps {
  items: Array<{
    id: number;
    name: string;
    color?: string | null;
    description?: string | null;
  }>;
  type: 'category' | 'list';
  maxVisible?: number;
  showColors?: boolean;
  className?: string;
  onEdit?: () => void;
}

function RelationsPills({
  items,
  type,
  maxVisible = 3,
  showColors = true,
  className = "",
  onEdit
}: RelationsPillsProps) {
  
  if (!items || items.length === 0) {
    return (
      <div className={`flex items-center gap-2 min-h-[24px] ${className}`}>
        <span className="text-muted-foreground text-xs italic">
          No {type === 'category' ? 'categories' : 'lists'}
        </span>
        {onEdit && (
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={onEdit}
            className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
            title={`Edit ${type === 'category' ? 'categories' : 'lists'}`}
          >
            <Edit3 className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  const visibleItems = items.slice(0, maxVisible);
  const remainingCount = Math.max(0, items.length - maxVisible);
  const hasMore = remainingCount > 0;

  return (
    <div className={`flex items-center gap-1 flex-wrap min-h-[24px] ${className}`}>
      {visibleItems.map(item => (
        <Badge 
          key={item.id}
          variant="secondary"
          className="text-xs h-6"
          style={showColors && item.color ? { 
            backgroundColor: `${item.color}20`,
            borderColor: item.color,
            color: item.color
          } : undefined}
          title={item.description || item.name}
        >
          {item.name}
        </Badge>
      ))}

      {hasMore && (
        <Badge 
          variant="outline"
          className="text-xs h-6 text-muted-foreground border-dashed"
          title={`${remainingCount} more ${type === 'category' ? 'categories' : 'lists'}: ${items.slice(maxVisible).map(item => item.name).join(', ')}`}
        >
          +{remainingCount} more...
        </Badge>
      )}

      {onEdit && (
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={onEdit}
          className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 ml-1"
          title={`Edit ${type === 'category' ? 'categories' : 'lists'}`}
        >
          <Edit3 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

// Exported Component for Category Relations
export function CategoryPills({ 
  categories, 
  onEdit, 
  className 
}: { 
  categories: ProfileCategoryInfo[];
  onEdit?: () => void;
  className?: string;
}) {
  // Map ProfileCategoryInfo to generic pill format
  const items = categories.map(cat => ({
    id: cat.categoryId,
    name: cat.categoryName,
    color: cat.categoryColor,
    description: null
  }));

  return (
    <RelationsPills
      items={items}
      type="category"
      maxVisible={3}
      showColors={true}
      onEdit={onEdit}
      className={className}
    />
  );
}

// Exported Component for List Relations
export function ListPills({ 
  lists, 
  onEdit, 
  className 
}: { 
  lists: ProfileListInfo[];
  onEdit?: () => void;
  className?: string;
}) {
  // Map ProfileListInfo to generic pill format
  const items = lists.map(list => ({
    id: list.listId,
    name: list.listName,
    color: null,
    description: list.listDescription
  }));

  return (
    <RelationsPills
      items={items}
      type="list"
      maxVisible={3}
      showColors={false}
      onEdit={onEdit}
      className={className}
    />
  );
}